import { useCallback, useEffect, useMemo, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import {
  createAdminAccount,
  listAdminAccounts,
  updateAdminAccount,
  type AdminAccountDTO,
} from '../features/admin-accounts';
import { ADMIN_ACCOUNT_PERMISSION_OPTIONS, getPermissionLabel } from '../lib/adminPermissions';
import { formatDateTime } from '../lib/format';

const STATUS_LABELS: Record<string, string> = {
  active: '正常',
  disabled: '已禁用',
};

const ERROR_MAP: Record<string, string> = {
  'admin account email is required': '请输入有效邮箱',
  'admin account password is required': '请输入密码',
  'admin account password is too short': '密码至少 8 位',
  'invalid admin account permission': '权限配置不合法',
  'full admin access required': '只有全权限管理员可以操作后台账号',
  'invalid admin account target': '管理员账号目标不合法',
  'user already exists': '邮箱已存在',
  network_error: '网络连接失败，请检查网络',
};

interface FormState {
  id?: number;
  email: string;
  nickname: string;
  password: string;
  status: 'active' | 'disabled';
  fullAccess: boolean;
  permissions: string[];
}

const initialForm: FormState = {
  email: '',
  nickname: '',
  password: '',
  status: 'active',
  fullAccess: false,
  permissions: ['assignments.view'],
};

function resolveError(error: unknown): string {
  if (isApiError(error)) {
    return ERROR_MAP[error.message] ?? `请求失败 (${error.message})`;
  }
  return '未知错误';
}

export function AdminAccountsPage() {
  const [items, setItems] = useState<AdminAccountDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(initialForm);

  const permissionOptions = useMemo(
    () => ADMIN_ACCOUNT_PERMISSION_OPTIONS.filter((item) => item.key !== 'dashboard'),
    [],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listAdminAccounts();
      setItems(result.items ?? []);
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => setForm(initialForm);

  const togglePermission = (permission: string) => {
    setForm((current) => {
      const exists = current.permissions.includes(permission);
      return {
        ...current,
        permissions: exists
          ? current.permissions.filter((item) => item !== permission)
          : [...current.permissions, permission],
      };
    });
  };

  const editAccount = (item: AdminAccountDTO) => {
    setForm({
      id: item.id,
      email: item.email,
      nickname: item.nickname,
      password: '',
      status: item.status,
      fullAccess: item.full_access || item.permissions?.includes('*') || !item.permissions?.length,
      permissions: item.permissions ?? [],
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!form.fullAccess && form.permissions.length === 0) {
        setError('请至少选择一项权限，或明确勾选全权限管理员');
        return;
      }
      const permissions = form.fullAccess ? ['*'] : form.permissions;
      if (form.id) {
        await updateAdminAccount(form.id, {
          nickname: form.nickname,
          password: form.password || undefined,
          status: form.status,
          permissions,
        });
      } else {
        await createAdminAccount({
          email: form.email,
          nickname: form.nickname,
          password: form.password,
          status: form.status,
          permissions,
        });
      }
      resetForm();
      await load();
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          创建后台子账号，并按页面控制访问范围。
        </p>
        <button
          type="button"
          onClick={load}
          className="h-9 px-3 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          刷新
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <input
            type="email"
            value={form.email}
            disabled={Boolean(form.id)}
            onChange={(event) => setForm((current) => ({ ...current, email: event.target.value.trim() }))}
            placeholder="邮箱"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <input
            type="text"
            value={form.nickname}
            onChange={(event) => setForm((current) => ({ ...current, nickname: event.target.value }))}
            placeholder="昵称"
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <input
            type="password"
            value={form.password}
            onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
            placeholder={form.id ? '新密码，不改留空' : '初始密码'}
            className="h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
          <select
            value={form.status}
            onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as FormState['status'] }))}
            className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="active">正常</option>
            <option value="disabled">禁用</option>
          </select>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <input
            id="admin-full-access"
            type="checkbox"
            checked={form.fullAccess}
            onChange={(event) => setForm((current) => ({ ...current, fullAccess: event.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label htmlFor="admin-full-access" className="text-sm text-gray-700">
            全部功能
          </label>
        </div>

        {!form.fullAccess && (
          <div className="mt-3 grid gap-2 md:grid-cols-3 xl:grid-cols-4">
            {permissionOptions.map((permission) => (
              <label
                key={permission.key}
                className="flex h-9 items-center gap-2 rounded-md border border-gray-200 px-3 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={form.permissions.includes(permission.key)}
                  onChange={() => togglePermission(permission.key)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span>{permission.label}</span>
              </label>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="h-9 px-4 rounded-md bg-primary text-white text-sm font-medium disabled:opacity-50 hover:bg-primary/90 transition-colors"
          >
            {form.id ? '保存修改' : '创建账号'}
          </button>
          {form.id && (
            <button
              type="button"
              onClick={resetForm}
              className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              取消编辑
            </button>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">暂无后台账号</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">账号</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">权限</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">创建时间</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-gray-900">
                        {item.nickname || '-'}
                        <span className="ml-1 text-xs text-gray-400">#{item.id}</span>
                      </div>
                      <div className="mt-1 text-xs text-gray-400 break-all">{item.email}</div>
                    </td>
                    <td className="px-4 py-3 align-top text-center">
                      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                        {STATUS_LABELS[item.status] ?? item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-gray-600">
                      {item.full_access ? (
                        <span className="text-sm text-primary">全部功能</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(item.permissions ?? []).map((permission) => (
                            <span key={permission} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                              {getPermissionLabel(permission)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-center text-xs text-gray-500 whitespace-nowrap">
                      {formatDateTime(item.created_at)}
                    </td>
                    <td className="px-4 py-3 align-top text-center">
                      <button
                        type="button"
                        onClick={() => editAccount(item)}
                        className="text-primary hover:underline"
                      >
                        编辑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
